import * as THREE from 'three';
import { ICharacterAI } from '../../interfaces/ICharacterAI';
import { Character } from '../Character';
import { World } from '../../world/World';
import { WeaponType } from '../../weapons/weapon-type';
import { PistolIdle } from '../states/PistolIdle';
import { PistolAimIdle } from '../states/PistolAimIdle';
import { PistolShoot } from '../states/PistolShoot';
import { RunPistol } from '../states/RunPistol';

type TacticalPhase = 'idle' | 'follow' | 'aim' | 'shoot';

export class TacticalCombatBehaviour implements ICharacterAI {
    public character: Character;

    private readonly world: World;
    private phase: TacticalPhase = 'idle';
    private stateApplied = false;
    private aimTimer = 0;
    private shootTimer = 0;

    private static readonly ENGAGE_DISTANCE = 8;
    private static readonly AIM_PAUSE_DURATION = 1;
    private static readonly MIN_AIM_PAUSE_DURATION = 0.2;
    private static readonly SHOOT_DURATION = 0.3;

    constructor(world: World) {
        this.world = world;
    }

    public update(timeStep: number): void {
        const target = this.world.player;
        if (!this.character || !target) { return; }
        if (this.character.isDead) { return; }

        if (!this.character.hasWeaponLoaded) {
            this.character.loadWeapon(WeaponType.Pistol);
            if (!this.stateApplied) {
                this.stateApplied = true;
                this.character.setState(new PistolIdle(this.character));
            }
            return;
        }

        if ((target as any)?.isDead) {
            this.character.triggerAction('up', false);
            this.transitionTo('idle');
            return;
        }

        const from = this.character.getWorldPosition(new THREE.Vector3());
        const to = target.getWorldPosition(new THREE.Vector3());
        const viewVector = new THREE.Vector3().subVectors(to, from).normalize();
        this.character.setViewVector(viewVector);
        this.character.setOrientation(viewVector);

        const distance = from.distanceTo(to);

        switch (this.phase) {
            case 'idle':
                this.transitionTo(distance > TacticalCombatBehaviour.ENGAGE_DISTANCE ? 'follow' : 'aim');
                break;

            case 'follow':
                this.character.triggerAction('up', true);
                if (distance <= TacticalCombatBehaviour.ENGAGE_DISTANCE) {
                    this.character.triggerAction('up', false);
                    this.aimTimer = 0;
                    this.transitionTo('aim');
                }
                break;

            case 'aim':
                this.character.triggerAction('up', false);
                if (distance > TacticalCombatBehaviour.ENGAGE_DISTANCE) {
                    this.transitionTo('follow');
                    break;
                }
                this.aimTimer += timeStep;
                if (this.aimTimer >= this.getAimPauseDuration(distance)) {
                    this.aimTimer = 0;
                    this.shootTimer = 0;
                    this.transitionTo('shoot');
                }
                break;

            case 'shoot':
                this.shootTimer += timeStep;
                if (this.shootTimer >= TacticalCombatBehaviour.SHOOT_DURATION) {
                    this.transitionTo(distance > TacticalCombatBehaviour.ENGAGE_DISTANCE ? 'follow' : 'aim');
                    this.aimTimer = 0;
                }
                break;
        }
    }

    private getAimPauseDuration(distance: number): number {
        const t = Math.max(0, Math.min(1, distance / TacticalCombatBehaviour.ENGAGE_DISTANCE));
        return TacticalCombatBehaviour.MIN_AIM_PAUSE_DURATION +
            t * (TacticalCombatBehaviour.AIM_PAUSE_DURATION - TacticalCombatBehaviour.MIN_AIM_PAUSE_DURATION);
    }

    private transitionTo(phase: TacticalPhase): void {
        if (this.phase === phase) { return; }
        this.phase = phase;
        switch (phase) {
            case 'idle':   this.character.setState(new PistolIdle(this.character)); break;
            case 'follow': this.character.setState(new RunPistol(this.character)); break;
            case 'aim':    this.character.setState(new PistolAimIdle(this.character)); break;
            case 'shoot':  this.character.setState(new PistolShoot(this.character)); break;
        }
    }
}
